/**
 * GradientCTA — canonical primary CTA: MUI Button with the Neon Dusk
 * coral gradient + glow hover. `loading` shows a spinner and implies
 * disabled. Sizes sm/md(default)/lg — paddings in SIZE_MAP below.
 */

import { forwardRef } from 'react';
import type { ReactNode, MouseEvent } from 'react';
import Button from '@mui/material/Button';
import type { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import type { SxProps, Theme } from '@mui/material/styles';
import { colors, designTokens, motion } from '../../theme/theme';

export interface GradientCTAProps extends Omit<ButtonProps, 'variant' | 'color' | 'size'> {
  /** Button label / icon + label children. */
  children: ReactNode;
  /** Click handler. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Disable the button. */
  disabled?: boolean;
  /** Show a loading spinner and dim the label. Implies disabled. */
  loading?: boolean;
  /** Visual size: 'sm' | 'md' (default) | 'lg'. */
  size?: 'sm' | 'md' | 'lg';
  /** Stretch to fill the parent. */
  fullWidth?: boolean;
}

const SIZE_MAP: Record<NonNullable<GradientCTAProps['size']>, { mui: ButtonProps['size']; padding: string; fontSize: string; spinner: number }> = {
  sm: { mui: 'small',  padding: '10px 20px', fontSize: '0.85rem', spinner: 16 },
  md: { mui: 'medium', padding: '12px 28px', fontSize: '0.95rem', spinner: 20 },
  lg: { mui: 'large',  padding: '14px 32px', fontSize: '1.05rem', spinner: 22 },
};

const GradientCTA = forwardRef<HTMLButtonElement, GradientCTAProps>(function GradientCTA(
  { children, onClick, disabled = false, loading = false, size = 'md', fullWidth = false, sx, ...rest },
  ref,
) {
  // eslint-disable-next-line security/detect-object-injection -- `size` is statically typed as 'sm'|'md'|'lg'; SIZE_MAP is total over that union. No user-controlled string indexing.
  const cfg = SIZE_MAP[size];
  const isDisabled = disabled || loading;

  const baseSx: SxProps<Theme> = {
    background: colors.primary.gradient,
    color: '#FFFFFF',
    padding: cfg.padding,
    fontSize: cfg.fontSize,
    fontWeight: 600,
    letterSpacing: '0.01em',
    textTransform: 'none',
    borderRadius: `${designTokens.radius.md}px`,
    boxShadow: 'none',
    transition: `transform ${motion.duration.fast} ${motion.easing.standard}, box-shadow ${motion.duration.base} ${motion.easing.standard}, background ${motion.duration.base} ${motion.easing.standard}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
      boxShadow: designTokens.shadow.glow,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: designTokens.shadow.focus,
    },
    '&.Mui-disabled': {
      background: colors.primary.gradient,
      color: '#FFFFFF',
      opacity: loading ? 0.85 : 0.5,
      boxShadow: 'none',
      transform: 'none',
    },
  };

  return (
    <Button
      ref={ref}
      variant="contained"
      size={cfg.mui}
      fullWidth={fullWidth}
      disabled={isDisabled}
      onClick={onClick}
      aria-busy={loading || undefined}
      sx={[baseSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...rest}
    >
      {loading ? (
        <>
          <CircularProgress
            size={cfg.spinner}
            thickness={5}
            sx={{ color: '#FFFFFF', mr: 1 }}
          />
          <span style={{ opacity: 0.7 }}>{children}</span>
        </>
      ) : (
        children
      )}
    </Button>
  );
});

export default GradientCTA;
